import {
  WEBSITE_JSON_LD,
  ORGANIZATION_JSON_LD,
  BREADCRUMB_JSON_LD,
} from "../config/seo";

export const generateWebPageStructuredData = ({
  title,
  description,
  url,
  publishedTime,
  modifiedTime,
}: {
  title: string;
  description: string;
  url: string;
  publishedTime?: string;
  modifiedTime?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: title,
  description,
  url,
  ...(publishedTime && { datePublished: publishedTime }),
  ...(modifiedTime && { dateModified: modifiedTime }),
  publisher: ORGANIZATION_JSON_LD,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": url,
  },
});

export const generateArticleStructuredData = ({
  title,
  description,
  url,
  publishedTime,
  modifiedTime,
  authorName,
  authorUrl,
  image,
}: {
  title: string;
  description: string;
  url: string;
  publishedTime: string;
  modifiedTime?: string;
  authorName: string;
  authorUrl: string;
  image: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  url,
  datePublished: publishedTime,
  ...(modifiedTime && { dateModified: modifiedTime }),
  author: {
    "@type": "Person",
    name: authorName,
    url: authorUrl,
  },
  publisher: ORGANIZATION_JSON_LD,
  image,
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": url,
  },
});

export const generateBreadcrumbStructuredData = (
  items: Array<{ name: string; item: string }>,
) => {
  return BREADCRUMB_JSON_LD(items);
};

export const generateOrganizationStructuredData = () => {
  return ORGANIZATION_JSON_LD;
};

export const generateWebsiteStructuredData = () => {
  return WEBSITE_JSON_LD;
};

export const generateProductListStructuredData = (
  products: Array<{
    id: number;
    name: string;
    description: string;
    category: string;
    image?: string;
    price?: number;
  }>,
  pageUrl: string,
) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "ECR Machines & POS Systems",
  url: pageUrl,
  numberOfItems: products.length,
  itemListElement: products.map((product, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Product",
      name: product.name,
      description: product.description,
      category: product.category,
      ...(product.image && { image: product.image }),
      brand: {
        "@type": "Brand",
        name: "R S Enterprises",
      },
      offers: {
        "@type": "Offer",
        availability: "https://schema.org/InStock",
        ...(product.price && {
          price: product.price,
          priceCurrency: "INR",
        }),
        seller: {
          "@type": "Organization",
          name: "R S Enterprises",
        },
      },
    },
  })),
});

// Brand is parsed from the product name prefix (Essae / WEP / Trucount …)
const parseBrand = (name: string): string => {
  const known = ["Essae", "WEP", "Trucount", "NGX", "Balaji", "Pixel", "Unisys", "Casio"];
  const match = known.find((brand) => name.toLowerCase().startsWith(brand.toLowerCase()));
  return match || "R S Enterprises";
};

export const generateProductStructuredData = (
  product: {
    name: string;
    description: string;
    category: string;
    image?: string;
    price?: number;
  },
  pageUrl: string,
  siteUrl: string,
) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  description: product.description,
  category: product.category,
  ...(product.image && {
    image: product.image.startsWith("http") ? product.image : `${siteUrl}${product.image}`,
  }),
  url: pageUrl,
  brand: {
    "@type": "Brand",
    name: parseBrand(product.name),
  },
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    url: pageUrl,
    ...(product.price && {
      price: product.price,
      priceCurrency: "INR",
    }),
    seller: {
      "@type": "Organization",
      name: "R S Enterprises",
    },
  },
});

export const generateLocalBusinessStructuredData = ({
  name,
  description,
  url,
  address,
  geo,
  telephone,
  openingHours,
  priceRange,
  image,
  servesCuisine,
  menu,
  acceptsReservations,
}: {
  name: string;
  description: string;
  url: string;
  address: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  geo: {
    latitude: string;
    longitude: string;
  };
  telephone: string;
  openingHours: string[];
  priceRange: string;
  image?: string;
  servesCuisine?: string[];
  menu?: string;
  acceptsReservations?: boolean;
}) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name,
  description,
  url,
  ...(image && { image }),
  address: {
    "@type": "PostalAddress",
    streetAddress: address.streetAddress,
    addressLocality: address.addressLocality,
    addressRegion: address.addressRegion,
    postalCode: address.postalCode,
    addressCountry: address.addressCountry,
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: geo.latitude,
    longitude: geo.longitude,
  },
  telephone,
  openingHours,
  priceRange,
  ...(servesCuisine && { servesCuisine }),
  ...(menu && { hasMenu: menu }),
  ...(acceptsReservations !== undefined && { acceptsReservations }),
});
